import axios from 'axios';
import { Settings as DateTimeSettings } from 'luxon';
import numbro from 'numbro';
import { createI18n } from 'vue-i18n';

import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { getAppInstance, setLanguage } from '@/globals';
import Validator from '@/validator/Validator';

export const dateTimeFormats = {
  en: {
    shortDate: { year: 'numeric', month: 'short', day: 'numeric' },
    fullDateTime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  fr: {
    shortDate: { year: 'numeric', month: 'short', day: 'numeric' },
    fullDateTime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  es: {
    shortDate: { year: 'numeric', month: 'short', day: 'numeric' },
    fullDateTime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  de: {
    shortDate: { year: 'numeric', month: 'short', day: 'numeric' },
    fullDateTime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  ja: {
    shortDate: { year: 'numeric', month: 'short', day: 'numeric' },
    fullDateTime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    },
  },
  zh: {
    shortDate: { year: 'numeric', month: 'short', day: 'numeric' },
    fullDateTime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    },
  },
};

dateTimeFormats['en-AU'] = dateTimeFormats.en;
dateTimeFormats['en-CA'] = dateTimeFormats.en;
dateTimeFormats['en-GB'] = dateTimeFormats.en;
dateTimeFormats['en-US'] = dateTimeFormats.en;
dateTimeFormats['fr-CA'] = dateTimeFormats.fr;
dateTimeFormats['fr-FR'] = dateTimeFormats.fr;
dateTimeFormats['de-DE'] = dateTimeFormats.de;
dateTimeFormats['ja-JP'] = dateTimeFormats.ja;
dateTimeFormats['zh-CN'] = dateTimeFormats.zh;
dateTimeFormats['es-MX'] = dateTimeFormats.es;

export const numberFormats = {
  'en-US': {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  'en-GB': {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  'en-CA': {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  'en-AU': {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  'fr-CA': {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  es: {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  'es-MX': {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  'fr-FR': {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  'de-DE': {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  'ja-JP': {
    currency: {
      style: 'currency',
      currency: 'USD',
    },
  },
  'zh-CN': {
    currency: {
      style: 'currency',
      currency: 'CNY',
    },
  },
};

const i18n = createI18n({
  legacy: false,
  locale: 'en-US',
  globalInjection: true,
  // messages: locales,
  dateTimeFormats,
  numberFormats,
  fallbackLocale: 'en',
  silentFallbackWarn: true,
  warnHtmlMessage: false,
});
export default i18n;

const loadedLanguages = [];

export const setI18nLanguage = (lang) => {
  if (getAppInstance()) {
    setLanguage(lang);
  }
  Validator.setLocale(lang);
  DateTimeSettings.defaultLocale = lang;
  if (i18n.mode === 'legacy') {
    i18n.global.locale = lang;
  } else {
    i18n.global.locale.value = lang;
  }

  numbro.setLanguage(lang);
  axios.defaults.headers.common['Accept-Language'] = lang;
  document.querySelector('html').setAttribute('lang', lang);
  GlobalEventBus.$emit(GlobalEvents.languageChanged);
  return lang;
};

export function resolveLanguage(lang) {
  const locale = {
    en: 'en',
    'en-AU': 'en',
    'en-CA': 'en',
    'en-GB': 'en',
    'en-US': 'en',
    fr: 'fr',
    'fr-CA': 'fr',
    'fr-FR': 'fr',
    de: 'ja',
    'de-DE': 'de',
    ja: 'ja',
    'ja-JP': 'ja',
    zh: 'zh',
    'zh-CN': 'zh',
    es: 'es',
    'es-MX': 'es',
  };

  return locale[lang];
}

export function loadLanguageAsync(lang) {
  const genericLang = resolveLanguage(lang);

  // If the language hasn't been loaded yet
  return import(`@/i18n/${genericLang}.js`).then((messages) => {
    i18n.global.setLocaleMessage(genericLang, messages.default);
    i18n.global.setLocaleMessage(lang, messages.default);
    loadedLanguages.push(lang);
    return setI18nLanguage(lang);
  });
}
